// ─── FICHE d'un budget (lecture) ──────────────────────────────────
// Demandé par Cédric le 15/08 : « ajouter un écran d'information quand on
// clique sur le budget ». Le principe app-wide — le TAP informe, le swipe →
// modifie — s'applique enfin aux enveloppes elles-mêmes : nom, payeurs et
// leurs parts, et ce que l'enveloppe contient, décomposé en débits/crédits.
//
// Les montants sont les NOMINAUX des lignes, toutes fréquences confondues :
// il n'y a pas de mois ici, donc ni inflation ni échéancier — c'est la
// carte d'identité de l'enveloppe, pas son suivi.
import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feuille, useUI, useT } from '@mop/core';
import { formatEuro } from './services/calculs';
import { lignesDeBudget } from './services/occurrences';
import { emojiBudget } from './data/categories';

export default function FicheBudget({
  visible, budget, lignes = [], onFermer, onEditer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  // ⚠️ Tous les hooks AVANT le moindre retour conditionnel (règle React).
  const b = budget || {};
  const mesLignes = useMemo(() => lignesDeBudget(lignes, b.id), [lignes, b.id]);
  const flux = useMemo(() => {
    let debits = 0, credits = 0, nbDebits = 0, nbCredits = 0;
    for (const l of mesLignes) {
      const m = Math.abs(Number(l.montant) || 0);
      if (l.sens === 'revenu') { credits += m; nbCredits += 1; }
      else { debits += m; nbDebits += 1; }
    }
    return { debits, credits, nbDebits, nbCredits };
  }, [mesLignes]);

  if (!visible || !budget) return null;
  const payeurs = (b.payeurs || []).filter(p => (p.pct || 0) > 0);

  return (
    <Feuille visible={visible} titre={b.nom || ''} emoji={emojiBudget(b)}
      hauteur={0.7} onFermer={onFermer}
      action={{ icone: '✏️', label: t('commun.modifier'), onPress: onEditer }}>

      {/* Les deux flux de l'enveloppe, face à face : c'est ce qui la résume
          le mieux — le net seul cacherait l'ampleur de chacun. */}
      <View style={s.bandeau}>
        <View style={s.fluxCol}>
          <Text style={s.fluxLbl}>{t('finances.graphRevenus')}</Text>
          <Text style={[s.fluxVal, { color: UI.ok || '#2e7d32' }]}>
            + {formatEuro(flux.credits)}
          </Text>
        </View>
        <View style={s.fluxCol}>
          <Text style={s.fluxLbl}>{t('finances.graphDepenses')}</Text>
          <Text style={[s.fluxVal, { color: UI.danger || '#c0392b' }]}>
            − {formatEuro(flux.debits)}
          </Text>
        </View>
      </View>
      <Text style={s.aide}>{t('finances.ficheBudgetNominal')}</Text>

      <View style={s.infos}>
        <Ligne s={s} lbl={t('finances.ficheLignesPrevues')}
          val={`${mesLignes.length}${flux.nbCredits
            ? `  ·  ${flux.nbDebits} −  /  ${flux.nbCredits} +` : ''}`} />
        {/* Les payeurs et leurs parts : le métier de l'enveloppe — qui doit
            combien. L'accès (qui la voit) reste dans sa feuille d'édition. */}
        <Ligne s={s} lbl={t('budget.payeurs')}
          val={payeurs.length
            ? payeurs.map(p => `${p.nom || p.uid} ${p.pct} %`).join('  ·  ')
            : '—'} />
      </View>
    </Feuille>
  );
}

const Ligne = ({ s, lbl, val }) => (
  <View style={s.infoLigne}>
    <Text style={s.infoLbl}>{lbl}</Text>
    <Text style={s.infoVal} numberOfLines={3}>{val}</Text>
  </View>
);

const makeStyles = (UI) => StyleSheet.create({
  bandeau: { flexDirection: 'row', justifyContent: 'space-evenly', paddingVertical: 8 },
  fluxCol: { alignItems: 'center' },
  fluxLbl: { fontSize: 11.5, color: UI.faint, fontWeight: '800', letterSpacing: 0.3, textTransform: 'uppercase' },
  fluxVal: { fontSize: 20, fontWeight: '800', marginTop: 2 },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, textAlign: 'center', marginBottom: 12 },
  infos: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12, paddingVertical: 4 },
  infoLigne: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingVertical: 8 },
  infoLbl: { fontSize: 12.5, color: UI.faint, fontWeight: '700', width: 110 },
  infoVal: { flex: 1, fontSize: 13.5, color: UI.ink, fontWeight: '600' },
});
